import { ApiError } from "@microsoft/kiota-abstractions";

export type ApiResult<T> =
    | { kind: "success"; value: T }
    | { kind: "apiError"; error: ApiError }
    | { kind: "networkError"; error: TypeError };

function isApiError(error: unknown): error is ApiError {
    return error instanceof Error && "responseStatusCode" in error;
}

/**
 * Wraps a Kiota client call, normalising thrown errors into a typed result instead of requiring
 * try/catch at every call site.
 *
 * - `apiError`: the server responded with a non-success status (see error.responseStatusCode).
 * - `networkError`: no response was ever received (dropped connection, DNS failure, etc.) —
 *   relies on the browser's default timeout for now; no custom timeout is configured.
 *
 * A successful empty-body response (e.g. 204) surfaces as `value === undefined` — there's no
 * reliable way to recover the actual status code for a successful call without bypassing Kiota's
 * typed deserialization (see NativeResponseWrapper), so we don't pretend to have one.
 */
export async function apiCall<T>(fn: () => Promise<T>): Promise<ApiResult<NonNullable<T>>> {
    try {
        return { kind: "success", value: (await fn()) as NonNullable<T> };
    }
    catch (error) {
        if (isApiError(error)) {
            if (error.responseStatusCode === 401 && !window.location.pathname.startsWith("/login")) {
                // Global 401 handling: the access token is missing/expired/rejected — bounce to
                // sign-in, preserving the current location as the post-login return target.
                const returnUrl = `${window.location.pathname}${window.location.search}`;
                window.location.assign(`/login?returnUrl=${encodeURIComponent(returnUrl)}`);
            }
            return { kind: "apiError", error };
        }
        if (error instanceof TypeError) {
            return { kind: "networkError", error };
        }
        throw error;
    }
}